/**
 * Coerce an unknown throwable into a real Error WITHOUT losing detail.
 * `String(obj)` yields "[object Object]" for POJOs (Supabase PostgrestError,
 * TanStack mutation rejections), discarding the message/code. This:
 *  - returns Errors unchanged,
 *  - lifts `.message` (+ preserves code/details/hint/status) off error-shaped
 *    objects,
 *  - JSON-stringifies other objects,
 *  - stringifies primitives.
 */
export function toError(value: unknown): Error {
  if (value instanceof Error) return value;
  if (typeof value === "string") return new Error(value);
  if (value !== null && typeof value === "object") {
    const o = value as {
      message?: unknown;
      code?: unknown;
      details?: unknown;
      hint?: unknown;
      status?: unknown;
    };
    if (typeof o.message === "string") {
      const e = new Error(o.message) as Error & Record<string, unknown>;
      if (o.code !== undefined) e.code = o.code;
      if (o.details !== undefined) e.details = o.details;
      if (o.hint !== undefined) e.hint = o.hint;
      if (o.status !== undefined) e.status = o.status;
      return e;
    }
    try {
      return new Error(JSON.stringify(value));
    } catch {
      return new Error(Object.prototype.toString.call(value));
    }
  }
  return new Error(String(value));
}
